import React from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { Theme, lighten } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Toolbar,
  Typography,
  IconButton,
  Tooltip
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";
import ConfirmDialog from "components/ConfirmDialog";
import useBoolean from "hooks/useBoolean";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight:
    theme.palette.mode === "light"
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
        },
  title: {
    flex: "1 1 100%",
  },
}));

type EnhancedTableToolbarProps = {
  title?: string;
  titleComponent?: React.ReactNode;
  selectedIds?: string[];
  onEdit?: (id: string) => void;
  onDelete?: (ids: string[]) => void;
  executeMultiple?: {
    dialogLabel: string;
    onExecuteMultiple: (ids: string[]) => Promise<void>;
    buttonLabel: string;
    icon?: React.ReactNode;
  };
};

const EnhancedTableToolbar = ({
  title,
  titleComponent,
  selectedIds = [],
  onEdit,
  onDelete,
  executeMultiple,
}: EnhancedTableToolbarProps) => {
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const [openExecuteMultipleDialog, setOpenExecuteMultipleDialog] = useBoolean(
    false
  );

  const numSelected = selectedIds.length;
  const handleEdit = () => onEdit!(selectedIds[0]);
  const handleDelete = () => onDelete!(selectedIds);
  const handleExecuteMultiple = async () => {
    await executeMultiple?.onExecuteMultiple!(selectedIds);
    setOpenExecuteMultipleDialog.setFalse();
  };

  const toolbarStyles = {
    ...styles.root,
    ...(numSelected > 0 ? styles.highlight : {}),
  };

  return (
    <>
      <Toolbar sx={toolbarStyles}>
        {numSelected > 0 ? (
          <Typography
            sx={styles.title}
            color="inherit"
            variant="subtitle1"
            component="div"
          >
            <T id="tableNumSelected" values={{ numSelected }} />
          </Typography>
        ) : (
          (title && (
            <Typography
              sx={styles.title}
              variant="h6"
              id="tableTitle"
              component="div"
            >
              {title}
            </Typography>
          )) ||
          titleComponent
        )}

        {onEdit && numSelected === 1 && (
          <Tooltip onClick={handleEdit} title={t({ id: "tableEditTooltip" })}>
            <IconButton aria-label={t({ id: "tableEditTooltip" })}>
              <EditIcon />
            </IconButton>
          </Tooltip>
        )}

        {onDelete && numSelected > 0 && (
          <Tooltip
            onClick={handleDelete}
            title={t({ id: "tableDeleteTooltip" }, { num: numSelected })}
          >
            <IconButton
              aria-label={t({ id: "tableDeleteTooltip" }, { num: numSelected })}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}

        {executeMultiple?.onExecuteMultiple && numSelected > 0 && (
          <Tooltip title={executeMultiple.buttonLabel}>
            <IconButton
              onClick={setOpenExecuteMultipleDialog.setTrue}
              aria-label={executeMultiple.buttonLabel}
            >
              {executeMultiple.icon}
            </IconButton>
          </Tooltip>
        )}
      </Toolbar>
      <ConfirmDialog
        dialogTitleKey="Confirm"
        contentKey={executeMultiple?.dialogLabel!}
        open={openExecuteMultipleDialog}
        onCancel={setOpenExecuteMultipleDialog.setFalse}
        handleConfirm={handleExecuteMultiple}
      />
    </>
  );
};

export default EnhancedTableToolbar;
